import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { RefreshCw, Download, List, Check, AlertTriangle } from 'lucide-react';

const YouTube = () => {
  const { API, fetchSettings } = useApp();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  const [settings, setSettings] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [selectedReleases, setSelectedReleases] = useState('');
  const [selectedVideos, setSelectedVideos] = useState('');
  const [manualReleasesId, setManualReleasesId] = useState('');
  const [manualVideosId, setManualVideosId] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/admin/settings`, { withCredentials: true });
        setSettings(response.data);
        setSelectedReleases(response.data.releases_playlist_id || '');
        setSelectedVideos(response.data.music_videos_playlist_id || '');
        setManualReleasesId(response.data.releases_playlist_id || '');
        setManualVideosId(response.data.music_videos_playlist_id || '');
      } catch (error) {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [API]);

  const hasYouTubeConfig = settings?.youtube_api_key && settings?.youtube_channel_id;

  const loadPlaylists = async () => {
    if (!hasYouTubeConfig) {
      toast.error('Please configure YouTube API first');
      return;
    }

    setLoadingPlaylists(true);
    try {
      const response = await axios.get(`${API}/admin/youtube/playlists`, { withCredentials: true });
      setPlaylists(response.data.playlists || []);
      toast.success(`Loaded ${response.data.playlists?.length || 0} playlists`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load playlists');
    } finally {
      setLoadingPlaylists(false);
    }
  };

  const savePlaylistSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, {
        releases_playlist_id: selectedReleases || manualReleasesId,
        music_videos_playlist_id: selectedVideos || manualVideosId
      }, { withCredentials: true });
      await fetchSettings();
      toast.success('Playlist settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const syncVideos = async () => {
    if (!settings?.releases_playlist_id && !settings?.music_videos_playlist_id) {
      toast.error('Please select at least one playlist first');
      return;
    }

    setSyncing(true);
    try {
      const response = await axios.post(`${API}/admin/youtube/sync`, {}, { withCredentials: true });
      await fetchSettings();
      toast.success(`Synced ${response.data.synced_count} videos`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync videos');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-youtube">
      <div>
        <h1 className="font-syne font-bold text-2xl text-white">YouTube Management</h1>
        <p className="text-white/50">Manage playlists and sync videos from your channel</p>
      </div>

      {/* Status Card */}
      {!hasYouTubeConfig && (
        <Card className="bg-brand-gold/10 border-brand-gold/30">
          <CardContent className="flex items-center gap-4 p-6">
            <AlertTriangle className="w-8 h-8 text-brand-gold flex-shrink-0" />
            <div>
              <h3 className="font-syne font-semibold text-white">YouTube API Not Configured</h3>
              <p className="text-white/60 text-sm">
                Please go to Settings and enter your YouTube API Key and Channel ID first.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Playlist Management */}
      <Card className="bg-brand-dark border-white/5">
        <CardHeader>
          <CardTitle className="font-syne text-white flex items-center gap-2">
            <List className="w-5 h-5 text-brand-red" />
            Playlist Selection
          </CardTitle>
          <CardDescription className="text-white/50">
            Select which playlists to use for Releases and Music Videos pages
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Load Playlists Button */}
          <Button
            onClick={loadPlaylists}
            disabled={loadingPlaylists || !hasYouTubeConfig}
            variant="outline"
            className="border-white/10 text-white hover:bg-white/5"
            data-testid="load-playlists-btn"
          >
            {loadingPlaylists ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Load Playlists from Channel
          </Button>

          {/* Playlist Dropdowns */}
          {playlists.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
              <div className="space-y-2">
                <Label className="text-white/70">Releases Playlist</Label>
                <Select value={selectedReleases} onValueChange={setSelectedReleases}>
                  <SelectTrigger className="bg-brand-charcoal border-white/10 text-white" data-testid="releases-playlist-select">
                    <SelectValue placeholder="Select a playlist" />
                  </SelectTrigger>
                  <SelectContent className="bg-brand-dark border-white/10">
                    {playlists.map(playlist => (
                      <SelectItem 
                        key={playlist.playlist_id} 
                        value={playlist.playlist_id}
                        className="text-white hover:bg-white/5"
                      >
                        {playlist.title} ({playlist.video_count} videos)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white/70">Music Videos Playlist</Label>
                <Select value={selectedVideos} onValueChange={setSelectedVideos}>
                  <SelectTrigger className="bg-brand-charcoal border-white/10 text-white" data-testid="videos-playlist-select">
                    <SelectValue placeholder="Select a playlist" />
                  </SelectTrigger>
                  <SelectContent className="bg-brand-dark border-white/10">
                    {playlists.map(playlist => (
                      <SelectItem 
                        key={playlist.playlist_id} 
                        value={playlist.playlist_id}
                        className="text-white hover:bg-white/5"
                      >
                        {playlist.title} ({playlist.video_count} videos)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Manual Input Fallback */}
          <div className="pt-4 border-t border-white/5">
            <p className="text-white/50 text-sm mb-4">Or enter playlist IDs manually:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-white/70">Releases Playlist ID</Label>
                <Input
                  value={manualReleasesId}
                  onChange={(e) => {
                    setManualReleasesId(e.target.value);
                    setSelectedReleases('');
                  }}
                  className="bg-brand-charcoal border-white/10 text-white"
                  placeholder="PL..."
                  data-testid="releases-playlist-manual"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Music Videos Playlist ID</Label>
                <Input
                  value={manualVideosId}
                  onChange={(e) => {
                    setManualVideosId(e.target.value);
                    setSelectedVideos('');
                  }}
                  className="bg-brand-charcoal border-white/10 text-white"
                  placeholder="PL..."
                  data-testid="videos-playlist-manual"
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <Button
            onClick={savePlaylistSettings}
            disabled={!hasYouTubeConfig}
            className="bg-brand-red hover:bg-brand-red-light"
            data-testid="save-playlists-btn"
          >
            <Check className="w-4 h-4 mr-2" />
            Save Playlist Settings
          </Button>
        </CardContent>
      </Card>

      {/* Sync Section */}
      <Card className="bg-brand-dark border-white/5">
        <CardHeader>
          <CardTitle className="font-syne text-white flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-brand-gold" />
            Sync Videos
          </CardTitle>
          <CardDescription className="text-white/50">
            Import videos from your selected playlists into the local cache
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings?.last_sync_at && (
            <p className="text-white/50 text-sm">
              Last sync: {new Date(settings.last_sync_at).toLocaleString()}
            </p>
          )}
          <Button
            onClick={syncVideos}
            disabled={syncing || !hasYouTubeConfig}
            className="bg-brand-gold hover:bg-brand-gold-light text-white"
            data-testid="sync-videos-btn"
          >
            {syncing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Sync Now
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default YouTube;
